<template>
  <div class="def-structure-edit-group">
    <div v-if="type !== 'dropdown'" class="d-flex flex-row gap10">
      <h5 class="text-left flex-grow-1">{{ structure[structureSettings.namePropGroup] }}</h5>
      <span v-if="!valid" class="requirement-text">{{ requirement }}</span>
    </div>

    <!-- Radio group: render all radio options, then children of selected -->
    <template v-if="type === 'radio'">
      <div class="flex-column">
        <StructureEditNode
          v-for="child in structure[structureSettings.childPropGroup]"
          :key="child[structureSettings.idPropNode]"
          v-model="nativeModel"
          :path="modelId"
          :structure="child"
          :type="'radio'"
          :selection-revokable="structure.minQty !== 0"
          :label="child[structureSettings.labelPropNode]"
          :structure-settings="structureSettings"
        />
      </div>
      <!-- Render children of selected radio option, if any -->
      <template v-if="selectedRadioChild">
        <div class="flex-column mt-2">
          <StructureEditGroup
            v-for="child in selectedRadioChild[structureSettings.childPropNode]"
            :key="child[structureSettings.idPropGroup]"
            v-model="nativeModel"
            :path="selectedRadioId"
            :structure="child"
            :structure-settings="structureSettings"
          />
        </div>
      </template>
    </template>

    <!-- Checkbox and other groups: render as before -->
    <template v-else-if="type !== 'dropdown'">
      <div :class="{'checkmark-error':!valid}" class="flex-column">
        <StructureEditNode
          ref="childGroup"
          v-for="child in structure[structureSettings.childPropGroup]"
          :key="child[structureSettings.idPropNode]"
          v-model="nativeModel"
          :path="modelId"
          :structure="child"
          :type="getChildType(child)"
          :selection-revokable="structure.minQty !== 0"
          :label="child[structureSettings.labelPropNode]"
          :structure-settings="structureSettings"
        />
      </div>
    </template>

    <div v-if="type==='dropdown'" :class="{'checkmark-error':!valid}">
      <Dropdown
        :options="convertDropdownElements(structure[structureSettings.childPropGroup])"
        :value="selection"
        :required="structure.minQty > 0"
        :label="structure[structureSettings.namePropGroup]"
        @input="handleSelectedOption"
      />
    </div>
  </div>
</template>

<script>
import Dropdown from "@/components/forms/Dropdown.vue";
export default {
  name: 'StructureEditGroup',
  components: {
    Dropdown: Dropdown,
    StructureEditNode: () => import("./StructureEditNode.vue"),
    StructureEditGroup: () => import("./StructureEditGroup.vue"),
  },
  data() {
    return {
      selection: undefined,
      active: false,
    };
  },
  event: ['update:modelValue'],
  model: {
    event: 'update:modelValue',
    prop: 'modelValue',
  },
  props: {
    id: String,
    path: {
      Type: String,
      default: () => '',
    },
    structure: {
      Type: Object,
      required: true,
    },
    value: {
      Type: Array,
      default: () => []
    },
    modelValue: {
      Type: Array,
      default: () => [],
    },
    structureSettings: {
      Type: Object,
      required: true,
    },
  },
  computed:{
    nativeModel: {
      get(){return this.modelValue;},
      set(value){this.$emit('update:modelValue', value);},
    },
    modelId() {
      return this.join(this.structure[this.structureSettings.idPropGroup]);
    },
    type() {
      let hasChildren = this.childHasChilds();
      if (this.structure.maxQty === 1 && this.structure.minQty <= 1) {
        return hasChildren ? 'radio' : 'dropdown';
      }
      return 'checkbox';
    },
    valid() {
      return typeof this.requirement === 'undefined';
    },
    requirement(){
      const chosenValues = this.getMeAndChildren(true, false);
      const isChosen = chosenValues.includes(this.modelId);
      const itemsSelected = chosenValues.length - (isChosen ? 1 : 0)
      if (this.structure.minQty <= itemsSelected && this.structure.maxQty >= itemsSelected)
        return undefined;
      if (this.structure.minQty > itemsSelected)
        return this.$t("text.subItemMin", [this.structure.minQty, itemsSelected]);
      if (this.structure.maxQty < itemsSelected)
        return this.$t("text.subItemMax", [this.structure.maxQty, itemsSelected]);
      return "Unknown Issue, Can not validate";
    },
    // --- Radio selection logic ---
    selectedRadioId() {
      if (this.type !== 'radio') return null;
      const prefix = this.modelId + '.';
      const selected = this.nativeModel.find(id => id.startsWith(prefix));
      return selected || null;
    },
    selectedRadioChild() {
      if (!this.selectedRadioId) return null;
      const idParts = this.selectedRadioId.split('.');
      const childId = idParts[idParts.length - 1];
      return this.structure[this.structureSettings.childPropGroup].find(
        child => String(child[this.structureSettings.idPropNode]) === childId && child[this.structureSettings.childPropNode] && child[this.structureSettings.childPropNode].length > 0
      );
    },
  },
  methods: {
    getValid() {
      // If this group is not valid, return false
      if (!this.valid) return false;
      // For radio: validate only the selected child group (if any)
      if (this.type === 'radio') {
        if (this.$refs.childGroup && this.selectedRadioChild) {
          // Only one child group is rendered for the selected radio
          // Find the StructureEditGroup(s) rendered for the selected radio child
          // Use $children to find StructureEditGroup instances
          const groupComps = this.$children.filter(
            c => c.$options.name === 'StructureEditGroup' && c.path === this.selectedRadioId
          );
          return groupComps.length === 0 ? true : groupComps.every(g => g.getValid());
        }
        return true;
      }
      // For checkbox/other: validate all child StructureEditNode refs
      if (this.$refs.childGroup) {
        // childGroup is an array of StructureEditNode
        return Array.isArray(this.$refs.childGroup)
          ? this.$refs.childGroup.every(node => node.getValid())
          : this.$refs.childGroup.getValid();
      }
      return true;
    },
    getChildType(item){
      let baseType = this.type;
      if (this.hasDirectChildren(item) && baseType !== "dropdown"){
        return baseType + "-boxed";
      }
      return baseType;
    },
    hasDirectChildren(item) {
      if (typeof this.structureSettings.hasChildPropNode === "string"){
        return item[this.structureSettings.hasChildPropNode];
      }
      if (item[this.structureSettings.childPropNode]) {
        return item[this.structureSettings.childPropNode].length > 0;
      }
      return false;
    },
    getChildId(item){
      return item[this.structureSettings.idPropNode];
    },
    childHasChilds(){
      for (let child of this.structure[this.structureSettings.childPropGroup]) {
        if (child[this.structureSettings.childPropNode] && child[this.structureSettings.childPropNode].length > 0) {
          return true;
        }
      }
      return false;
    },
    updateSelection(){
      const modelId = this.modelId;
      const currentModelSelectionChilds = this.getMeAndChildren(true);
      this.active = currentModelSelectionChilds.includes(modelId);
      if (this.type==='dropdown') {
        this.selection = this.getMeAndChildren(true, false)[0];
      }
    },
    join(id){
      if (this.path.length > 0)
        return this.path + '.' + id;
      return id;
    },
    getMeAndChildren(onlyDirect=true,includeMe=true){
      const modelId = this.modelId;
      return this.nativeModel
        .map(a=>""+a)
        .filter(a=>((a===modelId) && includeMe) || a.startsWith(modelId+'.'))
        .filter(a=>!onlyDirect||!a.replace(modelId,'').includes('.',1))
    },
    convertDropdownElements(items){
      const _self = this;
      let childIntepretor = function(item){return _self.modelId + '.' + _self.getChildId(item);};
      return items.map(a=>(
        {
          label: a[_self.structureSettings.labelPropNode],
          value: childIntepretor(a),
          disabled: false
        }
      ));
    },
    handleSelectedOption(value){
      let out = this.nativeModel;
      if (this.type === 'dropdown') {
        const parentId = this.modelId;
        out = out.filter(b => !b.startsWith(parentId + '.'));
      }
      out.push(value);
      this.nativeModel = out;
    },
  },
  watch:{
    'nativeModel': function (){
      this.updateSelection();
    },
  },
}
</script>
<style lang="scss">
@import '@/css/customizer';
.requirement-text { color: #ff6767; }
</style>
